/*
 * Service-worker registration + install-prompt plumbing.
 *
 * Exports:
 *   registerServiceWorker() — call once at app boot. Idempotent.
 *   getInstallPromptEvent() — returns the cached BeforeInstallPromptEvent
 *     so React components can trigger the native install UI.
 *   onInstallPromptChange(cb) — subscribe to install availability changes.
 */

let deferredPrompt = null;
const listeners = new Set();

function notify() {
  listeners.forEach((cb) => {
    try { cb(deferredPrompt); } catch { /* swallow */ }
  });
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    // Chrome fires this when the PWA is installable. Stash it so we can
    // show a custom CTA later instead of relying on the mini-infobar.
    event.preventDefault();
    deferredPrompt = event;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    notify();
  });
}

export function registerServiceWorker() {
  if (typeof window === 'undefined') return;
  if (!('serviceWorker' in navigator)) return;
  // The CRA dev server serves /sw.js fine, but skip when running inside
  // the visual-edit iframe to avoid colliding with their tooling.
  if (window.self !== window.top) return;

  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js', { scope: '/' })
      .then((reg) => {
        // Auto-skip-waiting when a new SW is installed in the background.
        // We don't show an update toast yet — the next page navigation will
        // pick up the new version transparently.
        if (reg.waiting) {
          reg.waiting.postMessage('SKIP_WAITING');
        }
        reg.addEventListener('updatefound', () => {
          const newWorker = reg.installing;
          if (!newWorker) return;
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              newWorker.postMessage('SKIP_WAITING');
            }
          });
        });
      })
      .catch(() => {
        // SW registration is best-effort; the app works fine without it.
      });
  });
}

export function getInstallPromptEvent() {
  return deferredPrompt;
}

export function onInstallPromptChange(cb) {
  listeners.add(cb);
  // Fire once so subscribers get current state immediately.
  try { cb(deferredPrompt); } catch { /* swallow */ }
  return () => listeners.delete(cb);
}

export async function triggerInstallPrompt() {
  if (!deferredPrompt) return { outcome: 'unavailable' };
  deferredPrompt.prompt();
  const choice = await deferredPrompt.userChoice;
  // The event can only be used once.
  deferredPrompt = null;
  notify();
  return choice;
}

export function isStandalone() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}
